<script>
import UserEditHeader from '@/components/page-user-edit/user-edit/UserEditHeader.vue';
import UserEditData from '@/components/page-user-edit/user-edit/UserEditData.vue';
import UserEditRole from '@/components/page-user-edit/user-edit/UserEditRole.vue';
import UserEditPassword from '@/components/page-user-edit/user-edit/UserEditPassword.vue';
import UserEditSessions from '@/components/page-user-edit/user-edit/UserEditSessions.vue';
import UserSubscriptionInfo from '@/components/page-user-edit/UserSubscriptionInfo.vue';
import UserStatistics from '@/components/page-user-edit/user-edit/UserStatistics.vue';
import UserPaymentHistory from '@/components/page-user-edit/user-edit/UserPaymentHistory.vue';
import UserSubscriptionHistory from '@/components/page-user-edit/user-edit/UserSubscriptionHistory.vue';
import UserEditHardDelete from '@/components/page-user-edit/user-edit/UserEditHardDelete.vue';
import UserEditPublisherAssignment from '@/components/page-user-edit/user-edit/UserEditPublisherAssignment.vue';
import ExpandableSection from '@/components/base-components/ExpandableSection.vue';

export default {
    components: {
        UserEditHeader,
        UserEditData,
        UserEditRole,
        UserEditPassword,
        UserEditSessions,
        UserSubscriptionInfo,
        UserStatistics,
        UserPaymentHistory,
        UserSubscriptionHistory,
        UserEditHardDelete,
        UserEditPublisherAssignment,
        ExpandableSection,
    },
    computed: {
        isShowUserEdit() {
            return this.$store.getters.IS_SHOW_USER_EDIT;
        },
        userSubscription() {
            const userData = this.$store.getters.USER_EDIT_DATA;
            return userData?.subscription || null;
        },
        userDetails() {
            return this.$store.getters.USER_DETAILS;
        },
        isUserDetailsLoading() {
            return this.$store.getters.IS_USER_DETAILS_LOADING;
        },
        userEditData() {
            return this.$store.getters.USER_EDIT_DATA;
        },
        isDev() {
            return process.env.VUE_APP_VERSION === 'dev';
        },
        canManagePublishers() {
            return !!this.$store.getters.USER_PERMISSIONS?.can_manage_publishers;
        },
    },
    watch: {
        'userEditData.id': {
            immediate: true,
            handler(newId) {
                if (newId && this.isShowUserEdit) {
                    this.$store.dispatch('loadUserDetails');
                }
            },
        },
        isShowUserEdit(newValue) {
            if (newValue && this.userEditData?.id) {
                // Load details when panel opens
                this.$store.dispatch('loadUserDetails');
            } else if (!newValue) {
                this.$store.commit('CLEAR_USER_DETAILS');
            }
        },
    },
    methods: {
        refreshUserDetails() {
            this.$store.dispatch('loadUserDetails');
        },
    },
};
</script>

<template>
    <div v-if="isShowUserEdit" class="left_additional_container">
        <!-- Always visible: Header and Primary Data -->
        <UserEditHeader />
        <hr class="hr_light_98" />
        <UserEditData />

        <!-- Expandable Sections (collapsed by default) -->
        <ExpandableSection
            title="Role and Permissions"
            icon="🔐"
            :defaultExpanded="false"
        >
            <UserEditRole />
        </ExpandableSection>

        <ExpandableSection
            v-if="canManagePublishers"
            title="Publisher Provider Assignments"
            icon="🏢"
            :defaultExpanded="false"
        >
            <UserEditPublisherAssignment />
        </ExpandableSection>

        <ExpandableSection
            title="Subscription Information"
            icon="📋"
            :defaultExpanded="false"
        >
            <UserSubscriptionInfo :subscription="userSubscription" />
        </ExpandableSection>

        <!-- User Statistics (async loaded) -->
        <ExpandableSection
            title="User Statistics"
            icon="📊"
            :defaultExpanded="false"
            :refreshable="true"
            :isLoading="isUserDetailsLoading"
            @refresh="refreshUserDetails"
        >
            <UserStatistics
                :details="userDetails"
                :isLoading="isUserDetailsLoading"
            />
        </ExpandableSection>

        <!-- Payment History (async loaded) -->
        <ExpandableSection
            title="Payment History"
            icon="💳"
            :defaultExpanded="false"
            :refreshable="true"
            :isLoading="isUserDetailsLoading"
            @refresh="refreshUserDetails"
        >
            <UserPaymentHistory
                :transactions="userDetails?.payment_transactions"
                :isLoading="isUserDetailsLoading"
            />
        </ExpandableSection>

        <!-- Subscription History (async loaded) -->
        <ExpandableSection
            title="Subscription History"
            icon="📜"
            :defaultExpanded="false"
            :refreshable="true"
            :isLoading="isUserDetailsLoading"
            @refresh="refreshUserDetails"
        >
            <UserSubscriptionHistory
                :history="userDetails?.subscription_history"
                :isLoading="isUserDetailsLoading"
            />
        </ExpandableSection>

        <ExpandableSection
            title="Change Password"
            icon="🔑"
            :defaultExpanded="false"
        >
            <UserEditPassword />
        </ExpandableSection>

        <ExpandableSection
            title="Active Sessions"
            icon="💻"
            :defaultExpanded="false"
        >
            <UserEditSessions />
        </ExpandableSection>

        <ExpandableSection
            v-if="isDev"
            title="Danger Zone"
            icon="⚠️"
            :defaultExpanded="false"
        >
            <UserEditHardDelete />
        </ExpandableSection>
    </div>
</template>
